import { useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { gsap } from "gsap";
import { Card } from "@/ui/components";
import { Input } from "@/ui/components/primitives/input";
import { Label } from "@/ui/components/primitives/label";
import { Button } from "@/ui/components/primitives/button";
import { Alert, AlertDescription } from "@/ui/components/primitives/alert";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { showErrorToast, showSuccessToast } from "@/services/toast-helper";
import { AlertCircle, RefreshCw, WifiOff } from "lucide-react";
import { classifyAuthError, getErrorAction, AuthErrorType } from "@/lib/error-types";
import { getStoredRememberMePreference } from "@/services/api/services/auth";

// OTP Input Component
function OTPInput({ value, onChange, onComplete }: {
  value: string;
  onChange: (val: string) => void;
  onComplete: () => void;
}) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);

  const handleInput = (val: string, index: number) => {
    const numbersOnly = val.replace(/\D/g, '').slice(-1);

    // Ensure we're properly updating the value array
    const currentValueArray = value.split('');
    currentValueArray[index] = numbersOnly;
    const newValue = currentValueArray.join('');
    onChange(newValue);

    // Only proceed if we have a valid digit
    if (numbersOnly) {
      // Pop animation for digit
      gsap.fromTo(
        inputRefs.current[index],
        { scale: 1 },
        { scale: 1.2, duration: 0.15, yoyo: true, repeat: 1, ease: "power2.out" }
      );

      // Auto advance to next input
      if (index < 5) {
        inputRefs.current[index + 1]?.focus();
      } else {
        // When 6th digit is entered, check if we have all 6 digits
        setTimeout(() => {
          const updatedValue = currentValueArray.join('');
          console.log('6th digit entered, value:', updatedValue, 'length:', updatedValue.length);
          if (updatedValue.length === 6 && updatedValue[5]) {
            console.log('Auto-submitting OTP:', updatedValue);
            const validInputs = inputRefs.current.filter(Boolean) as HTMLElement[];
            gsap.to(validInputs, {
              scale: 1.05,
              duration: 0.15,
              stagger: 0.05,
              ease: "power2.out",
              yoyo: true,
              repeat: 1,
              onComplete: () => {
                console.log('Animation complete, triggering onComplete');
                onComplete();
              }
            });
          }
        }, 100);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleFocus = (index: number) => {
    setFocusedIndex(index);
    // Glow effect
    gsap.to(inputRefs.current[index], {
      boxShadow: "0 0 20px rgba(154, 79, 225, 0.5)",
      duration: 0.2,
      ease: "power2.out"
    });
  };

  const handleBlur = (index: number) => {
    setFocusedIndex(-1);
    gsap.to(inputRefs.current[index], {
      boxShadow: "0 0 0px rgba(154, 79, 225, 0)",
      duration: 0.2,
      ease: "power2.out"
    });
  };

  return (
    <div className="flex justify-center gap-2">
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[index] || ''}
          onChange={(e) => handleInput(e.target.value, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onFocus={() => handleFocus(index)}
          onBlur={() => handleBlur(index)}
          className={`w-12 h-12 text-center text-lg font-semibold border-3 rounded-lg transition-all duration-200 focus:outline-none`}
          style={{
            borderColor: focusedIndex === index
              ? 'rgb(139, 29, 248)'
              : (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'rgba(139, 29, 248, 0.3)' : '#d1d5db'),
            backgroundColor: focusedIndex === index
              ? (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'rgba(139, 29, 248, 0.2)' : '#ffffff')
              : (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'rgba(53, 53, 53, 0.71)' : '#ffffff'),
            color: typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'white' : 'black',
            boxShadow: focusedIndex === index
              ? '0 0 0 3px rgba(139, 29, 248, 0.2), 0 0 15px rgba(139, 29, 248, 0.3)'
              : (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? '0 0 0 3px rgba(139, 29, 248, 0.1)' : '0 0 0 3px rgba(0, 0, 0, 0.1)')
          }}
        />
      ))}
    </div>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState<{ message: string; type: AuthErrorType; action?: string } | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [rememberMeDefault, setRememberMeDefault] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [isFlipping, setIsFlipping] = useState(false);

  const cardWrapperRef = useRef<HTMLDivElement>(null);
  const frontCardRef = useRef<HTMLDivElement>(null);
  const backCardRef = useRef<HTMLDivElement>(null);

  // Retrieve stored Remember Me preference on component mount
  useEffect(() => {
    const storedPreference = getStoredRememberMePreference();
    setRememberMeDefault(storedPreference);

    // Cleanup on unmount
    return () => {
      if (cardWrapperRef.current) {
        gsap.killTweensOf(cardWrapperRef.current);
      }
    };
  }, []);

  const { control, handleSubmit, formState: { errors, isSubmitting } } = useForm<{
    email: string;
    password: string;
    mfaCode?: string;
    rememberMe: boolean;
  }>({
    defaultValues: { email: "", password: "", mfaCode: "", rememberMe: rememberMeDefault }
  });

  const flipToMFA = async () => {
    if (!cardWrapperRef.current || !frontCardRef.current || !backCardRef.current) return;

    setIsFlipping(true);

    // Set MFA state before animation
    setMfaRequired(true);

    // Animate the flip
    gsap.to(cardWrapperRef.current, {
      rotationY: 180,
      duration: 0.6,
      ease: "power2.inOut",
      onComplete: () => {
        setIsFlipping(false);
        // Focus first OTP input
        setTimeout(() => {
          const firstInput = document.querySelector('input[type="text"][inputMode="numeric"]') as HTMLInputElement;
          firstInput?.focus();
        }, 100);
      }
    });
  };

  const flipBackToLogin = async () => {
    if (!cardWrapperRef.current) return;

    setIsFlipping(true);

    gsap.to(cardWrapperRef.current, {
      rotationY: 0,
      duration: 0.6,
      ease: "power2.inOut",
      onComplete: () => {
        setMfaRequired(false);
        setOtpValue("");
        setIsFlipping(false);
      }
    });
  };

  const onSubmit = async (data: { email: string; password: string; mfaCode?: string; rememberMe: boolean }) => {
    // Prevent multiple submissions
    if (isSubmitting || isFlipping) return;

    setError(null);

    try {
      const res = await login(data.email, data.password, data.rememberMe, mfaRequired ? otpValue : undefined);

      if (!res) {
        setError({
          message: "Login failed",
          type: AuthErrorType.SERVER_ERROR,
          action: "Please try again or contact support."
        });
        return;
      }

      showSuccessToast("Logged in", "Welcome back");

      // Fade out the card without flip animation
      if (cardWrapperRef.current) {
        gsap.to(cardWrapperRef.current, {
          opacity: 0,
          y: -20,
          duration: 0.2,
          ease: "power2.out",
          onComplete: () => {
            // Navigate immediately after fade starts
            if (res.role === "superadmin") {
              navigate("/approvals");
            } else {
              navigate("/dashboard");
            }
          }
        });
      } else {
        // Immediate navigation if ref is not available
        if (res.role === "superadmin") {
          navigate("/approvals");
        } else {
          navigate("/dashboard");
        }
      }
    } catch (err: any) {
      const authError = classifyAuthError(err);

      // Handle MFA required case
      if (authError.type === AuthErrorType.MFA_REQUIRED) {
        await flipToMFA();
        return;
      }

      setError({
        message: authError.message,
        type: authError.type,
        action: getErrorAction(authError) || undefined
      });
      showErrorToast("Login failed", authError.message);
    }
  };

  const handleRetry = async () => {
    setIsRetrying(true);
    setError(null);

    // Simulate retry with delay
    setTimeout(() => {
      setIsRetrying(false);
    }, 1000);
  };

  const getErrorIcon = (type: AuthErrorType) => {
    switch (type) {
      case AuthErrorType.NETWORK_ERROR:
      case AuthErrorType.TIMEOUT_ERROR:
        return <WifiOff className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const handleOTPComplete = () => {
    console.log('OTP Complete triggered with value:', otpValue);
    // Only submit if we have exactly 6 digits
    if (otpValue && otpValue.length === 6) {
      console.log('Submitting form with 6-digit OTP');
      // Trigger form submission with OTP
      handleSubmit(onSubmit)();
    } else {
      console.log('OTP incomplete, not submitting');
    }
  };

  return (
    <div
      className="login-bg min-h-screen flex items-center justify-center p-4 relative"
      role="main"
      aria-labelledby="login-title"
      style={{
        backgroundImage: 'url(/loginBg.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="absolute inset-0 bg-black/40" aria-hidden="true" />

      <div
        className="relative z-10"
        style={{
          perspective: "1200px",
          width: "100%",
          maxWidth: "360px"
        }}
      >
        <div
          ref={cardWrapperRef}
          className="relative w-full"
          style={{
            transformStyle: "preserve-3d",
            height: "500px" // Fixed height to prevent layout shifts
          }}
        >
          {/* Front Face - Login Form */}
          <Card
            ref={frontCardRef}
            className="absolute top-0 left-0 w-full p-6 sm:p-8 border-2 border-purple-200 dark:border-purple-800 shadow-2xl"
            style={{
              backfaceVisibility: "hidden",
              transform: "rotateY(0deg)",
              transformStyle: "preserve-3d"
            }}
          >
            <div className="space-y-6">
              <div className="text-left">
                <h1 id="login-title" className="text-2xl font-bold">Sign In</h1>
                <p className="text-muted-foreground mt-2">
                  Access your Backoffice
                </p>
              </div>

              {error && !mfaRequired && (
                <Alert
                  variant="destructive"
                  role="alert"
                  aria-live="assertive"
                  className="border-destructive/50 bg-destructive/5"
                >
                  {getErrorIcon(error.type)}
                  <AlertDescription className="text-sm">
                    <div className="space-y-1">
                      <p>{error.message}</p>
                      {error.action && (
                        <p className="text-xs opacity-90">{error.action}</p>
                      )}
                      {(error.type === AuthErrorType.NETWORK_ERROR ||
                        error.type === AuthErrorType.TIMEOUT_ERROR ||
                        error.type === AuthErrorType.SERVER_ERROR) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRetry}
                          loading={isRetrying}
                          loadingText="Retrying..."
                          disabled={isSubmitting || isFlipping}
                          className="mt-2 h-7 text-xs"
                        >
                          <RefreshCw className="h-3 w-3 mr-1" />
                          Retry
                        </Button>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              <form
                className="space-y-6"
                onSubmit={handleSubmit(onSubmit)}
                noValidate
              >
                <div>
                  <Controller
                    name="email"
                    control={control}
                    rules={{ required: "Email is required", pattern: { value: /[^@\s]+@[^@\s]+\.[^@\s]+/, message: "Enter a valid email" } }}
                    render={({ field }) => (
                      <Input
                        id="email"
                        type="email"
                        placeholder="Email"
                        autoComplete="email"
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? "email-error" : undefined}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                      />
                    )}
                  />
                  {errors.email && (
                    <p id="email-error" className="text-destructive text-sm mt-2">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <Controller
                    name="password"
                    control={control}
                    rules={{ required: "Password is required", minLength: { value: 8, message: "Minimum 8 characters" } }}
                    render={({ field }) => (
                      <Input
                        id="password"
                        type="password"
                        placeholder="Password"
                        autoComplete="current-password"
                        aria-invalid={!!errors.password}
                        aria-describedby={errors.password ? "password-error" : undefined}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                      />
                    )}
                  />
                  {errors.password && (
                    <p id="password-error" className="text-destructive text-sm mt-2">{errors.password.message}</p>
                  )}
                </div>

                <div className="flex items-center">
                  <Controller
                    name="rememberMe"
                    control={control}
                    render={({ field }) => (
                      <>
                        <label
                          htmlFor="rememberMe"
                          onClick={() => field.onChange(!field.value)}
                          className={`
                            w-5 h-5 rounded border cursor-pointer transition-all duration-200 flex items-center justify-center flex-shrink-0
                            bg-white border-2 border-black dark:bg-gray-900 dark:border-gray-500
                            hover:border-purple-500 hover:bg-gray-50 dark:hover:bg-gray-800
                            relative z-10 peer
                            ${field.value ? 'bg-purple-600 border-purple-600' : ''}
                          `}
                          style={{ position: 'relative', zIndex: 10 }}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              field.onChange(!field.value);
                            }
                          }}
                        >
                          <input
                            id="rememberMe"
                            type="checkbox"
                            className="absolute opacity-0"
                            checked={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                          />

                          {/* White Tick - only visible when checked */}
                          <svg
                            className="w-4 h-4 opacity-0 peer-checked:opacity-100 transition-opacity"
                            fill="none"
                            stroke="white"
                            strokeWidth="3"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M20 6L9 17l-5-5"
                            />
                          </svg>
                        </label>
                        <Label
                          htmlFor="rememberMe"
                          className="text-sm text-gray-600 cursor-pointer select-none hover:text-gray-800 transition-colors ml-3"
                        >
                          Stay logged in
                        </Label>
                      </>
                    )}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    loading={isSubmitting || isFlipping}
                    loadingText="Signing in..."
                    className="flex-1"
                  >
                    Sign in
                  </Button>
                </div>
              </form>
            </div>
          </Card>

          {/* Back Face - MFA Form */}
          <Card
            ref={backCardRef}
            className="absolute top-0 left-0 w-full p-6 sm:p-8 border-2 border-purple-200 dark:border-purple-800 shadow-2xl"
            style={{
              backfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
              transformStyle: "preserve-3d"
            }}
          >
            <div className="space-y-6">
              <div className="text-left">
                <h1 id="login-title" className="text-2xl font-bold">Enter MFA Code</h1>
                <p className="text-muted-foreground mt-2">
                  Enter your 6-digit authentication code
                </p>
              </div>

              {error && mfaRequired && (
                <Alert
                  variant="destructive"
                  role="alert"
                  aria-live="assertive"
                  className="border-destructive/50 bg-destructive/5"
                >
                  {getErrorIcon(error.type)}
                  <AlertDescription className="text-sm">
                    <div className="space-y-1">
                      <p>{error.message}</p>
                      {error.action && (
                        <p className="text-xs opacity-90">{error.action}</p>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              <form
                className="space-y-6"
                onSubmit={handleSubmit(onSubmit)}
                noValidate
              >
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-3 block">Authentication Code</Label>
                    <OTPInput
                      value={otpValue}
                      onChange={setOtpValue}
                      onComplete={handleOTPComplete}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={flipBackToLogin}
                    className="text-sm text-purple-600 hover:text-purple-700 transition-colors"
                    disabled={isFlipping}
                  >
                    ← Back to login
                  </button>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    loading={isSubmitting || isFlipping}
                    loadingText={isSubmitting ? "Verifying..." : "Processing..."}
                    className="flex-1"
                    disabled={otpValue.length !== 6 || isSubmitting || isFlipping}
                  >
                    Verify
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}